import { useEffect, useRef } from "react";
import { MEDIA_URL } from "../../axios";
import musicPlayerStore from "../../zustand/musicPlayer.store";

const useMediaSession = () => {
  const lastSongId = useRef(null);
  const positionTimer = useRef(null);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;

    const updatePosition = () => {
      const el = window.__4playEngine?.getCurrentAudioRef()?.current;
      if (!el || isNaN(el.duration) || !isFinite(el.duration)) return;
      try {
        navigator.mediaSession.setPositionState({
          duration: el.duration,
          playbackRate: el.playbackRate || 1,
          position: Math.min(el.currentTime, el.duration),
        });
      } catch (_) {}
    };

    const unsub = musicPlayerStore.subscribe((state) => {
      const { songs, currentSongIndex, isPlaying, searchedSong } = state;
      const song = searchedSong || songs?.[currentSongIndex];
      if (!song) return;

      if (song.id !== lastSongId.current) {
        lastSongId.current = song.id;
        const artwork = song.imageFileName
          ? [{ src: `${MEDIA_URL}/songs_images/${song.imageFileName}`, sizes: "512x512", type: "image/jpeg" }]
          : [];

        navigator.mediaSession.metadata = new MediaMetadata({
          title: song.title || "Unknown",
          artist: song.artist || "",
          album: "4Play",
          artwork,
        });

        setTimeout(updatePosition, 500);
      }

      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
    });

    positionTimer.current = setInterval(updatePosition, 5000);

    navigator.mediaSession.setActionHandler("play", () => {
      musicPlayerStore.getState().setIsPlaying(true);
      const engine = window.__4playEngine;
      if (engine) {
        const el = engine.getCurrentAudioRef()?.current;
        if (el) {
          engine.setGainValue(engine.getCurrentGain(), 1);
          el.muted = false;
          el.play().catch(() => {});
        }
      }
    });

    navigator.mediaSession.setActionHandler("pause", () => {
      musicPlayerStore.getState().setIsPlaying(false);
      window.__4playEngine?.getCurrentAudioRef()?.current?.pause();
    });

    navigator.mediaSession.setActionHandler("previoustrack", () => {
      const { songs, currentSongIndex, setCurrentSongIndex } = musicPlayerStore.getState();
      if (!songs?.length) return;
      const prev = (currentSongIndex - 1 + songs.length) % songs.length;
      setCurrentSongIndex(prev);
    });

    navigator.mediaSession.setActionHandler("nexttrack", () => {
      const { songs, currentSongIndex, setCurrentSongIndex } = musicPlayerStore.getState();
      if (!songs?.length) return;
      const next = (currentSongIndex + 1) % songs.length;
      setCurrentSongIndex(next);
    });

    try {
      navigator.mediaSession.setActionHandler("seekto", (details) => {
        const el = window.__4playEngine?.getCurrentAudioRef()?.current;
        if (el && details.seekTime != null) {
          el.currentTime = details.seekTime;
          updatePosition();
        }
      });
    } catch (_) {}

    return () => {
      unsub();
      if (positionTimer.current) clearInterval(positionTimer.current);
    };
  }, []);
};

export default useMediaSession;
